import { BRAND_NAME } from "../../constants/brand";
import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Checkbox, Divider, FormControlLabel, Grid, InputAdornment, Link, Typography, useMediaQuery } from '@mui/material';
import signinStyle from '../../Styles/ComponentStyles/Signin.module.scss';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import ReCAPTCHA from "react-google-recaptcha";
import globalStyles from "../../Styles/ComponentStyles/global.module.scss"
import newRBlogo from '../../assets/Images/mar-lockup-white.svg';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import MovingIcon from '@mui/icons-material/Moving';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GlobalTextfield from '../../GlobalComponent/GlobalTextfield/GlobalTextfield';
import agent from '../../service/api';
import { LEGAL, agreementAcceptanceText } from '../../config/legal';
import { registrationPayload } from './registrationPayload';

const INITIAL_user_licenses_held = {
    life: false,
    series_6: false,
    series_7: false,
    series_65: false,
    other: false,
    none: false,
} as const;

type HeldLicenseKey = keyof typeof INITIAL_user_licenses_held;

const LICENSE_CHECKBOXES: { id: HeldLicenseKey; label: string }[] = [
    { id: 'life', label: 'Life' },
    { id: 'series_6', label: 'Series 6' },
    { id: 'series_7', label: 'Series 7' },
    { id: 'series_65', label: 'Series 65' },
    { id: 'other', label: 'Other' },
    { id: 'none', label: 'None' },
];

const HELD_LICENSE_LABEL_BY_ID = Object.fromEntries(
    LICENSE_CHECKBOXES.map(({ id, label }) => [id, label])
) as Record<HeldLicenseKey, string>;

const points = [
    { img: MovingIcon, text: "Income Gap Supplementation" },
    { img: CalculateOutlinedIcon, text: "Roth Conversion Analysis" },
    { img: ShieldOutlinedIcon, text: "Qualified Account Spend Down" },
];

// ── Inline error helper ──────────────────────────────────────────────────────
const FieldError = ({ message }: { message: string }) =>
    message ? (
        <Typography
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                color: '#d32f2f',
                mt: '4px',
                ml: '2px',
            }}
        >
            {message}
        </Typography>
    ) : null;
// ────────────────────────────────────────────────────────────────────────────

const EMAIL_REGEX = /^([a-zA-Z0-9\._]+)@([a-zA-Z0-9])+.([a-z]+)(.[a-z]+)?$/;
const PHONE_DIGITS_REGEX = /^\d{10}$/;

const INITIAL_FIELD_ERRORS = {
    first_name: '',
    last_name: '',
    email_address: '',
    contact_number: '',
    user_licenses_held: '',
    user_agreement_checkbox: '',
    re_captcha: '',
};

const NewUserRegistration = () => {
    const navigate = useNavigate();
    const isLessThan1024 = useMediaQuery('(max-width:1024px)');

    const [registerFormDetails, setRegisterFormDetails] = useState<any>({
        first_name: "",
        last_name: "",
        email_address: "",
        contact_number: "",
        user_role_id: null,
        user_licenses_held: { ...INITIAL_user_licenses_held },
        user_agreement_checkbox: false,
    });
    const [apiError, setApiError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({ ...INITIAL_FIELD_ERRORS });

    const {
        setFlagLoader,
        getUserRoleDetails
    } = useContext(UserContext) as UserContextPayload;

    // ── Clear a single field's error as soon as the user types / checks ───────
    const clearFieldError = (fieldName: string) => {
        setFieldErrors((prev) => ({ ...prev, [fieldName]: '' }));
    };

    const handleFieldValueChange = (fieldName: any, event: any) => {
        const { type, value, checked } = event.target;
        const fieldValue = type === "checkbox" ? checked : value;
        clearFieldError(fieldName);
        setRegisterFormDetails((prevDetails: any) => ({
            ...prevDetails,
            [fieldName]: fieldValue,
        }));
    };

    const handleLicenseCheckboxChange = (licenseId: HeldLicenseKey, checked: boolean) => {
        clearFieldError('user_licenses_held');
        setRegisterFormDetails((prevDetails: any) => {
            const prevLicenses = prevDetails.user_licenses_held || { ...INITIAL_user_licenses_held };
            if (licenseId === "none") {
                if (checked) {
                    return { ...prevDetails, user_licenses_held: { ...INITIAL_user_licenses_held, none: true } };
                }
                return { ...prevDetails, user_licenses_held: { ...prevLicenses, none: false } };
            }
            const next = { ...prevLicenses, [licenseId]: checked, none: false };
            return { ...prevDetails, user_licenses_held: next };
        });
    };

    const getUserLicensesHeldAsStringArray = (): string[] => {
        const licenses = registerFormDetails.user_licenses_held || {};
        return (Object.keys(INITIAL_user_licenses_held) as HeldLicenseKey[])
            .filter((key) => licenses[key])
            .map((key) => HELD_LICENSE_LABEL_BY_ID[key]);
    };

    // ── Validate and collect all field errors at once ─────────────────────────
    const validate = (): boolean => {
        const {
            first_name,
            last_name,
            email_address,
            contact_number,
            user_agreement_checkbox,
            re_captcha,
        } = registerFormDetails;
        const userLicensesHeldPayload = getUserLicensesHeldAsStringArray();

        const errors = { ...INITIAL_FIELD_ERRORS };
        let isValid = true;

        if (!first_name?.trim()) {
            errors.first_name = 'First name is required';
            isValid = false;
        }
        if (!last_name?.trim()) {
            errors.last_name = 'Last name is required';
            isValid = false;
        }
        if (!email_address?.trim()) {
            errors.email_address = 'Email address is required';
            isValid = false;
        } else if (!EMAIL_REGEX.test(email_address)) {
            errors.email_address = 'Invalid email address';
            isValid = false;
        }
        if (!contact_number?.trim()) {
            errors.contact_number = 'Phone number is required';
            isValid = false;
        } else if (!PHONE_DIGITS_REGEX.test(contact_number.trim())) {
            errors.contact_number = 'Invalid phone number';
            isValid = false;
        }
        if (userLicensesHeldPayload.length === 0) {
            errors.user_licenses_held = 'Please select at least one license (or None)';
            isValid = false;
        }
        if (!user_agreement_checkbox) {
            errors.user_agreement_checkbox = 'You must agree to the User Agreement';
            isValid = false;
        }
        if (!re_captcha) {
            errors.re_captcha = 'Please complete the reCAPTCHA verification';
            isValid = false;
        }

        setFieldErrors(errors);
        return isValid;
    };

    const handleNewUserRegister = async () => {
        setApiError('');
        if (!validate()) return;

        try {
            setFlagLoader(true);
            const userRoleDetailResponse = await getUserRoleDetails();
            if (userRoleDetailResponse?.status) {
                const agentRoleDetail = userRoleDetailResponse?.data?.find(
                    (user: any) => user?.user_role_type?.toLowerCase() === "agent"
                );
                // the body carries the User Agreement version the checkbox named (RB-326); built in registrationPayload.ts
                const payload = registrationPayload(registerFormDetails, agentRoleDetail?.user_role_id, getUserLicensesHeldAsStringArray());
                const newUserRegisterResponse = await agent.newUserRegisterAPIV2.post(payload);
                if (newUserRegisterResponse?.status) {
                    setFlagLoader(false);
                    navigate('/registration-success');
                } else {
                    setFlagLoader(false);
                    setApiError(newUserRegisterResponse?.error?.message || 'Error registering new user!');
                }
            } else {
                setFlagLoader(false);
                setApiError(userRoleDetailResponse?.error?.message || 'Something went wrong!');
            }
        } catch (error) {
            setFlagLoader(false);
            setApiError(error instanceof Error ? error.message : 'Something went wrong!');
            console.log(error, "Error Registering User!");
        } finally {
            setFlagLoader(false);
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const renderRegistrationContent = (isSmallScreen: boolean) => {
        const titleCls = isSmallScreen ? signinStyle.welcomeTextForSmallScreen : signinStyle.welcomeText;
        const subCls = isSmallScreen ? signinStyle.rightSideSubTextForSmallScreen : signinStyle.rightSideSubText;
        const checkboxCls = isSmallScreen ? signinStyle.signinCheckboxLabelForSmallScreen : signinStyle.signinCheckboxLabel;
        const linkColor = isSmallScreen ? '#A3C9FF' : '#3B78CE';
        const disclaimerHeadingCls = isSmallScreen ? signinStyle.disclaimerHeadingTextForSmallScreen : signinStyle.disclaimerHeadingText;
        const disclaimerTextCls = isSmallScreen ? signinStyle.disclaimerTextForSmallScreen : signinStyle.disclaimerText;

        return (
            <Grid container gap={3}>
                <Grid item xs={12}>
                    <Grid container alignItems="center" className={signinStyle.welcomeTextContainer}>
                        <Grid item xs={12}>
                            <Typography component={'span'} className={titleCls}>
                                Join {BRAND_NAME}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography className={subCls}>
                                Start planning your retirement journey today
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>

                {apiError && (
                    <Grid item xs={12}>
                        <Box className={signinStyle.apiErrorBox}>
                            <Box className={signinStyle.apiErrorIconWrapper}>
                                <ErrorOutlineIcon fontSize="small" />
                            </Box>
                            <Typography className={signinStyle.apiErrorText}>
                                {apiError}
                            </Typography>
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Grid container gap={"18px"}>
                        {/* First Name / Last Name */}
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                                    <GlobalTextfield
                                        required
                                        variant="outlined"
                                        placeholder='First Name'
                                        fullWidth
                                        stylingClassName={globalStyles.globalPFTextfield}
                                        autoComplete='off'
                                        value={registerFormDetails.first_name}
                                        error={Boolean(fieldErrors.first_name)}
                                        onChange={(e) => handleFieldValueChange("first_name", e)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonOutlineIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <FieldError message={fieldErrors.first_name} />
                                </Grid>
                                <Grid item xs={12} sm={isSmallScreen ? 12 : 6}>
                                    <GlobalTextfield
                                        required
                                        variant="outlined"
                                        placeholder='Last Name'
                                        fullWidth
                                        stylingClassName={globalStyles.globalPFTextfield}
                                        autoComplete='off'
                                        value={registerFormDetails.last_name}
                                        error={Boolean(fieldErrors.last_name)}
                                        onChange={(e) => handleFieldValueChange("last_name", e)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonOutlineIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <FieldError message={fieldErrors.last_name} />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Email */}
                        <Grid item xs={12}>
                            <GlobalTextfield
                                required
                                variant="outlined"
                                placeholder='Email Address'
                                fullWidth
                                stylingClassName={globalStyles.globalPFTextfield}
                                value={registerFormDetails.email_address}
                                error={Boolean(fieldErrors.email_address)}
                                onChange={(e) => handleFieldValueChange("email_address", e)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FieldError message={fieldErrors.email_address} />
                        </Grid>

                        {/* Phone */}
                        <Grid item xs={12}>
                            <GlobalTextfield
                                required
                                variant="outlined"
                                placeholder='Phone Number'
                                fullWidth
                                stylingClassName={globalStyles.globalPFTextfield}
                                value={registerFormDetails.contact_number}
                                error={Boolean(fieldErrors.contact_number)}
                                onChange={(e) => handleFieldValueChange("contact_number", e)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocalPhoneIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FieldError message={fieldErrors.contact_number} />
                        </Grid>

                        {/* Licenses */}
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    border: '1px solid #E5E7EB',
                                    borderRadius: 1,
                                    p: 1.5,
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Typography
                                    component="div"
                                    sx={{ fontWeight: 500, fontSize: '14px', color: '#364153' }}
                                >
                                    What Licenses do you hold?{' '}
                                    <Typography component="span" sx={{ color: '#810505' }}>*</Typography>
                                </Typography>
                                <Grid container direction="column">
                                    {LICENSE_CHECKBOXES.map(({ id, label }) => (
                                        <Grid item xs={12} key={id}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={Boolean(registerFormDetails.user_licenses_held?.[id])}
                                                        onChange={(e) => handleLicenseCheckboxChange(id, e.target.checked)}
                                                    />
                                                }
                                                label={label}
                                                className={checkboxCls}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                            <FieldError message={fieldErrors.user_licenses_held} />
                        </Grid>

                        {/* User Agreement */}
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    border: '1px solid #E5E7EB',
                                    borderRadius: 1,
                                    p: 1.5,
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={registerFormDetails.user_agreement_checkbox}
                                            onChange={(e) => handleFieldValueChange("user_agreement_checkbox", e)}
                                        />
                                    }
                                    label={
                                        <span data-testid="agreement-acceptance">
                                            {agreementAcceptanceText()}{' '}
                                            <Link
                                                href={LEGAL.termsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontSize: 'inherit', textDecoration: 'underline' }}
                                            >
                                                (read it)
                                            </Link>
                                        </span>
                                    }
                                    className={checkboxCls}
                                />{' '}
                                <Typography component={'span'} sx={{ color: '#810505' }}>*</Typography>
                            </Box>
                            <FieldError message={fieldErrors.user_agreement_checkbox} />
                        </Grid>

                        {/* reCAPTCHA */}
                        <Grid item xs={12} className={globalStyles.captchaItem}>
                            <ReCAPTCHA
                                sitekey="6LftXpkqAAAAAOHtYQv1WJt_7Kq9m1asUWeeAahO"
                                onChange={(token: any) => {
                                    clearFieldError('re_captcha');
                                    setRegisterFormDetails((prev: any) => ({ ...prev, re_captcha: token }));
                                }}
                            />
                            <FieldError message={fieldErrors.re_captcha} />
                        </Grid>

                        {/* Submit */}
                        <Grid item xs={12}>
                            <Grid container gap={2} justifyContent={"center"}>
                                <Grid item xs={12}>
                                    <Button
                                        variant='contained'
                                        size='large'
                                        fullWidth
                                        className={signinStyle.signinBtn}
                                        onClick={() => handleNewUserRegister()}
                                    >
                                        Register
                                    </Button>
                                </Grid>
                                <Grid item xs={12} textAlign={"center"}>
                                    <span className={signinStyle.noAccTxt}>
                                        Already have an account?{' '}
                                        <span
                                            style={{ color: linkColor, cursor: "pointer", textDecoration: "underline" }}
                                            onClick={() => navigate('/login')}
                                        >
                                            Sign in
                                        </span>
                                    </span>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Divider sx={{ opacity: 0.8, borderColor: "#E5E7EB", height: "1px", width: "100%", margin: "0 auto" }} />

                <Grid item xs={12}>
                    <Grid container gap={1}>
                        <Grid item xs={12}>
                            <Typography className={disclaimerHeadingCls}>Disclaimer</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography className={disclaimerTextCls}>
                                This presentation is a general discussion of the topic presented, based on our current understanding of applicable tax laws, regulations, and rulings.
                                This information and presentation in no way should be interpreted as investment or tax advice. In actual practice, the transactions discussed may be more complex and will require the attention and expertise of professional tax and legal advisors.
                                In order to comply with certain U.S. Treasury regulations, please be advised of the following: unless expressly stated otherwise, any reference to U.S. Federal tax information contained in these materials,
                                including any attachments, is not intended or written to be used, and cannot be used, by any person for the purpose of avoiding any penalties that may be imposed
                                by the Internal Revenue Service. Values and benefits are not guaranteed unless stated as guaranteed on illustrations.
                                Examples are used to illustrate the potential benefits of indexed universal life insurance / IRS SEC. 7702 & 72(e).
                                Portions of this presentation are based on NAIC compliant life insurance illustrations which must be present during the presentation and fully reviewed
                                for additional disclosures.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography className={disclaimerTextCls}>
                                        © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography component="div" className={disclaimerTextCls}>
                                        <Link href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: isSmallScreen ? '#fff' : '#3B78CE', fontSize: 'inherit' }}>
                                            Terms and Conditions
                                        </Link>
                                        {' | '}
                                        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: isSmallScreen ? '#fff' : '#3B78CE', fontSize: 'inherit' }}>
                                            Privacy Policy
                                        </Link>
                                        {' | '}
                                        <Link href="/user-agreement" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: isSmallScreen ? '#fff' : '#3B78CE', fontSize: 'inherit' }}>
                                            User Agreement
                                        </Link>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            <Grid
                container
                direction="column"
                className={signinStyle.signinMainContainer}
                sx={{
                    ...(isLessThan1024 && {
                        background: 'linear-gradient(135deg, rgba(4, 56, 93, 0.9) 0%, rgba(4, 56, 93, 0.8) 50%, rgba(59, 120, 206, 0.7) 100%)',
                    }),
                }}
            >
                <Grid item xs={12} className={signinStyle.signinMainContainerItem}>
                    <Grid container wrap="nowrap" alignItems="stretch" className={signinStyle.signinMainContainerRow}>
                        {isLessThan1024 ? (
                            <Grid item xs={12} className={signinStyle.smallScreenStyle} alignItems={"flex-start"}>
                                <Grid container className={signinStyle.smallScreenRightMainContainer}>
                                    <Box component="img" alt="Logo" src={newRBlogo} />
                                    <Grid item xs={12} className={signinStyle.welcomeContainer}>
                                        {renderRegistrationContent(true)}
                                    </Grid>
                                </Grid>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={6} className={signinStyle.leftSection}>
                                    <Grid container gap={4} padding={"100px"}>
                                        <Grid item xs={12}>
                                            <Box sx={{ position: 'absolute', top: 0, left: 0, zIndex: 2, padding: "50px 100px" }}>
                                                <Box component="img" alt="Logo" src={newRBlogo} />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container className={signinStyle.pointsContainer}>
                                                <Grid item xs={12}>
                                                    <Typography component='span' className={signinStyle.title}>
                                                        Turn Complex Retirement Planning into Simple Visual Storytelling
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography className={signinStyle.subText}>
                                                        A powerful platform combining interactive financial calculators with innovative products for agents and consumers.
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} className={signinStyle.pointsWrapper}>
                                                    <Grid container direction="column" gap={2}>
                                                        {points.map((item, index) => (
                                                            <Grid item xs={12} key={index}>
                                                                <Grid container className={signinStyle.pointItem}>
                                                                    <Grid item className={signinStyle.iconWrapper}>
                                                                        <item.img sx={{ color: "#fff" }} />
                                                                    </Grid>
                                                                    <Grid item className={signinStyle.textWrapper}>
                                                                        {item.text}
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={6} className={signinStyle.rightSection} alignItems={"flex-start"}>
                                    <Grid container className={signinStyle.rightMainContainer}>
                                        <Grid item xs={8} className={signinStyle.welcomeContainer}>
                                            {renderRegistrationContent(false)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default NewUserRegistration;